import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useAppSelector } from "hooks/storeHooks"
import { UploadBulkProducts } from "pages/Products/UploadBulkProducts/UploadBulkProducts"
import React, { useState } from "react"
import { IntegrationType } from "types"
import { AddProductStepOne } from "./AddProductStepOne"
import { SelectProductsEcommerceStepTwo } from "pages/Products/AddProductsModal/SelectProductsEcommersStepTwo/SelectProductsEcommerceStepTwo"
import { useTranslation } from "react-i18next"
import { type ProductCategories } from "components/Navbar/types"

interface StepContentProps {
  step: number
  userType: IntegrationType
  selectedModalType: string
  category: ProductCategories
  onAlert: (data: { error: boolean; text: string | React.ReactNode }) => void
  onClose: () => void
  handleGoToStepTwo: (type?: string) => void
  handleGoToStepFirst: (type?: string) => void
  hideBack: boolean
}

const StepContent: React.FC<StepContentProps> = ({
  step,
  userType,
  selectedModalType,
  category,
  onAlert,
  onClose,
  handleGoToStepTwo,
  handleGoToStepFirst,
  hideBack,
}) => {
  if (step === 1) return <AddProductStepOne handleGoToStepTwo={handleGoToStepTwo} />
  if (step === 2 && userType !== IntegrationType.COMMON)
    return selectedModalType === "from-store" ? (
      <SelectProductsEcommerceStepTwo category={category} />
    ) : (
      <UploadBulkProducts
        onAlert={onAlert}
        backHandler={() => handleGoToStepFirst()}
        onClose={onClose}
        hideBack={hideBack}
      />
    )

  return (
    <UploadBulkProducts
      onAlert={onAlert}
      backHandler={() => handleGoToStepFirst()}
      onClose={onClose}
      hideBack={hideBack}
    />
  )
}

interface AddProductsModalProps {
  onAlert: (data: { error: boolean; text: string | React.ReactNode }) => void
  onClose: () => void
  isOpen: boolean
  category: ProductCategories
  initialStep?: 1 | 2
  initialType?: "bulk" | "from-store"
}

export const AddProductsModal: React.FC<AddProductsModalProps> = ({
  onAlert,
  onClose,
  isOpen,
  category,
  initialStep,
  initialType,
}) => {
  const userType = useAppSelector((state) => state.user.userInfo.integrationType)
  const [step, setStep] = useState(initialStep ?? (userType !== IntegrationType.COMMON ? 2 : 1))
  const { t } = useTranslation()
  const [selectedModalType, setSelectedModalType] = useState<string>(
    initialType ?? (userType !== IntegrationType.COMMON ? "from-store" : "bulk")
  )

  const handleGoToStepTwo = (type?: string) => {
    if (type) setSelectedModalType(type)
    setStep(2)
  }
  const handleGoToStepFirst = (type?: string) => {
    if (type) setSelectedModalType(type)
    setStep(1)
  }

  const isBulkView = step === 2 && selectedModalType === "bulk"
  const hideBack = initialStep === 2
  const title = isBulkView
    ? t("productsPage.uploadProductList")
    : t("addProductsModal.selectProduct")
  const description = isBulkView
    ? t("uploadBulkProducts.saveTimeByAddingProducts")
    : t("addProductStepOne.title", { defaultValue: "Choose how you want to add products" })

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="add-products-modal">
        <DialogHeader>
          <DialogTitle data-testid="add-products-modal-title">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <StepContent
          step={step}
          userType={userType}
          selectedModalType={selectedModalType}
          category={category}
          onAlert={onAlert}
          onClose={onClose}
          handleGoToStepTwo={handleGoToStepTwo}
          handleGoToStepFirst={handleGoToStepFirst}
          hideBack={hideBack}
        />
      </DialogContent>
    </Dialog>
  )
}
